import { useState, useEffect, useCallback } from 'react';
import { Box, Toolbar } from '@mui/material';
import { Outlet, useNavigate } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { Breadcrumb } from './Breadcrumb';
import { OnboardingWizard } from '@/components/common/OnboardingWizard';
import { useWorkspaceStore } from '@/store';
import { workspaceApi } from '@/api/services';

const DRAWER_WIDTH = 260;

/**
 * Main application layout with sidebar and header
 * Used for authenticated routes
 */
export const AppLayout: React.FC = () => {
  const navigate = useNavigate();
  const setWorkspaces = useWorkspaceStore((s) => s.setWorkspaces);
  const setSelectedWorkspace = useWorkspaceStore((s) => s.setSelectedWorkspace);
  const clearWorkspaces = useWorkspaceStore((s) => s.clearWorkspaces);
  const [showOnboarding, setShowOnboarding] = useState(false);

  // On mount: fetch real workspaces from server and sync store
  useEffect(() => {
    workspaceApi.getWorkspaces()
      .then((serverWorkspaces) => {
        setWorkspaces(serverWorkspaces);
        if (serverWorkspaces.length === 0) {
          clearWorkspaces();
          setShowOnboarding(true);
        }
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreateWorkspace = useCallback(async (name: string, organizationId?: string) => {
    const workspace = await workspaceApi.createWorkspace({
      workspaceName: name,
      workspaceDescription: '',
      organizationId,
    });
    const updated = await workspaceApi.getWorkspaces();
    setWorkspaces(updated);
    if (workspace) {
      setSelectedWorkspace(workspace);
      navigate(`/w/${workspace.workspaceSlug}`);
    }
  }, [navigate, setWorkspaces, setSelectedWorkspace]);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <Header />
      <Sidebar />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          bgcolor: 'grey.50',
          minHeight: '100vh',
        }}
      >
        <Toolbar /> {/* Spacer for fixed AppBar */}
        <Breadcrumb />
        <Outlet />
      </Box>

      <OnboardingWizard
        open={showOnboarding}
        onClose={() => setShowOnboarding(false)}
        onCreateWorkspace={handleCreateWorkspace}
      />
    </Box>
  );
};

export default AppLayout;
